import React, { useEffect } from "react";
import { Button } from "./ui/button";
import { Download, X, Loader2 } from "lucide-react";

/**
 * Modal viewer PDF intégré (iframe + blob URL).
 *
 * Avantage : indépendant du viewer natif du navigateur (Brave/Firefox/Norton qui forcent download).
 * Le PDF est affiché dans une iframe qui sait qu'elle reçoit un blob `application/pdf`,
 * donc il s'affiche inline systématiquement.
 *
 * Props :
 *   - open: boolean → ouvre le modal
 *   - onClose: () => void
 *   - blob: Blob | null → contenu PDF (binary)
 *   - filename: string → nom de fichier pour le bouton "Télécharger"
 *   - loading: boolean → état de génération
 *   - title?: string → titre du modal (défaut "Aperçu PDF")
 */
export function PdfViewerModal({ open, onClose, blob, filename, loading, title }) {
  const [objUrl, setObjUrl] = React.useState(null);

  useEffect(() => {
    if (!blob) {
      setObjUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleDownload = () => {
    if (!objUrl) return;
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || "document.pdf";
    a.click();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-black/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="pdf-viewer-modal"
    >
      {/* Barre supérieure */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#1A1C1A] border-b border-[#3D2914] flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-white font-medium text-sm truncate">{title || "Aperçu PDF"}</span>
          {filename && <span className="text-[#A3A6A3] text-xs font-mono truncate hidden sm:inline">· {filename}</span>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            onClick={handleDownload}
            disabled={!objUrl || loading}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="pdf-viewer-download"
          >
            <Download className="w-4 h-4 mr-2" /> Télécharger
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/10"
            data-testid="pdf-viewer-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Zone PDF */}
      <div className="flex-1 overflow-hidden bg-[#2A2A2A]">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-white">
            <Loader2 className="w-8 h-8 animate-spin text-[#8C6B2F]" />
            <span className="text-sm text-[#A3A6A3]">Génération du PDF…</span>
          </div>
        ) : objUrl ? (
          <iframe
            src={`${objUrl}#toolbar=1&navpanes=0&zoom=page-fit`}
            className="w-full h-full border-0"
            title={title || "Aperçu PDF"}
            data-testid="pdf-viewer-iframe"
          />
        ) : (
          <div className="h-full flex items-center justify-center text-white text-sm">
            Aucun document à afficher.
          </div>
        )}
      </div>
    </div>
  );
}

export default PdfViewerModal;
